(() => {
  'use strict';

  if (window.__rdVaradaBooksLoaded) return;
  window.__rdVaradaBooksLoaded = true;

  const WISHLIST_KEY = 'rd-varada-wishlist-v1';
  const WISHLIST_META_KEY = 'rd-varada-wishlist-meta-v1';
  const productCache = new Map();

  const parseJSON = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (error) {
      return fallback;
    }
  };

  const getWishlist = () => {
    const parsed = parseJSON(window.localStorage.getItem(WISHLIST_KEY) || '[]', []);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    if (Array.isArray(parsed?.items)) return parsed.items.map(String).filter(Boolean);
    return [];
  };

  const saveWishlist = (items) => {
    const unique = [...new Set(items.map(String).filter(Boolean))];
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(unique));
    } catch (error) {
      // Keep the UI usable when storage is unavailable.
    }
    document.dispatchEvent(new CustomEvent('rd:wishlist:changed', { detail: { items: unique } }));
    return unique;
  };

  const getWishlistMeta = () => {
    const parsed = parseJSON(window.localStorage.getItem(WISHLIST_META_KEY) || '{}', {});
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  };

  const saveWishlistMeta = (meta) => {
    try {
      window.localStorage.setItem(WISHLIST_META_KEY, JSON.stringify(meta));
    } catch (error) {
      // A metadata snapshot is optional; the product endpoint remains the fallback.
    }
  };

  const updateWishlistUI = () => {
    const items = getWishlist();
    const count = items.length;

    document.querySelectorAll('[data-rd-wishlist-count]').forEach((element) => {
      element.textContent = count > 99 ? '99+' : String(count);
      element.hidden = count === 0;
    });

    document.querySelectorAll('[data-rd-wishlist-toggle]').forEach((button) => {
      const handle = String(button.dataset.productHandle || button.dataset.productId || '');
      const active = Boolean(handle && items.includes(handle));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const card = button.closest('[data-rd-book-card]');
      const title = card?.querySelector('.rd-book-card__title')?.textContent?.trim() || 'this book';
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);
    });
  };

  const installWishlistFallback = () => {
    if (window.__rdVaradaWishlistInitialised) {
      updateWishlistUI();
      return;
    }

    window.__rdVaradaWishlistInitialised = true;

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rd-wishlist-toggle]');
      if (!button) return;

      const handle = String(button.dataset.productHandle || button.dataset.productId || '');
      if (!handle) return;

      event.preventDefault();
      const items = getWishlist();
      const next = items.includes(handle) ? items.filter((item) => item !== handle) : [...items, handle];
      saveWishlist(next);
    });

    document.addEventListener('rd:wishlist:changed', updateWishlistUI);
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_KEY) updateWishlistUI();
    });

    updateWishlistUI();
  };

  const readCardSnapshot = (card) => {
    const script = card?.querySelector('[data-rd-book-snapshot]');
    if (!script) return null;
    return parseJSON(script.textContent || '', null);
  };

  const syncSnapshotAfterWishlistClick = (event) => {
    const button = event.target.closest('[data-rd-wishlist-toggle]');
    if (!button) return;

    const card = button.closest('[data-rd-book-card]');
    const snapshot = readCardSnapshot(card);
    const handle = String(button.dataset.productHandle || snapshot?.handle || '');
    if (!handle) return;

    window.requestAnimationFrame(() => {
      const items = getWishlist();
      const meta = getWishlistMeta();

      if (items.includes(handle) && snapshot) {
        meta[handle] = snapshot;
      } else if (!items.includes(handle) && meta[handle]) {
        delete meta[handle];
      }

      saveWishlistMeta(meta);
    });
  };

  const escapeHTML = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const normalizeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getCurrency = () => window.Shopify?.currency?.active || 'INR';

  const formatMoney = (amount) => {
    const numeric = Number(amount || 0) / 100;
    const hasDecimals = Math.round(numeric) !== numeric;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: getCurrency(),
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      }).format(numeric);
    } catch (error) {
      return `₹${numeric.toFixed(hasDecimals ? 2 : 0)}`;
    }
  };

  const getProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`${normalizeRoot()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load ${handle}`);
        return response.json();
      })
      .catch(() => null);

    productCache.set(handle, request);
    return request;
  };

  const firstAvailableVariant = (product) =>
    product?.variants?.find((variant) => variant.available) || product?.variants?.[0] || null;

  const optionValue = (product, variant, names) => {
    if (!product?.options || !variant) return '';
    const targetNames = names.map((name) => name.toLowerCase());

    for (let index = 0; index < product.options.length; index += 1) {
      const option = product.options[index];
      const optionName = String(option?.name || option || '').toLowerCase().trim();
      if (!targetNames.includes(optionName)) continue;
      return variant[`option${index + 1}`] || option?.values?.[0] || '';
    }

    return '';
  };

  const productBadge = (product) => {
    const tags = Array.isArray(product?.tags)
      ? product.tags
      : String(product?.tags || '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);

    for (const tag of tags) {
      const lower = tag.toLowerCase();
      if (lower.startsWith('badge:')) return tag.split(':').slice(1).join(':').trim();
      if (['top 50', 'bestseller', 'best seller'].includes(lower)) return tag;
    }

    return '';
  };

  const combineProductData = (handle, product, snapshot, config) => {
    const variant = firstAvailableVariant(product);
    const productImage =
      variant?.featured_image?.src ||
      product?.featured_image ||
      product?.images?.[0] ||
      snapshot?.image ||
      '';

    const price = Number(variant?.price ?? product?.price ?? snapshot?.price ?? 0);
    const compareAtPrice = Number(
      variant?.compare_at_price ?? product?.compare_at_price ?? snapshot?.compareAtPrice ?? 0,
    );
    const discountAmount = Math.max(compareAtPrice - price, 0);
    const discountPercent = compareAtPrice > price ? Math.round((discountAmount * 100) / compareAtPrice) : 0;

    return {
      handle,
      id: product?.id || snapshot?.id || handle,
      title: product?.title || snapshot?.title || handle,
      url: product?.url || snapshot?.url || `${normalizeRoot()}products/${handle}`,
      image: productImage,
      author: snapshot?.author || '',
      language: snapshot?.language || optionValue(product, variant, ['language']),
      publisher:
        snapshot?.publisher ||
        optionValue(product, variant, ['publisher', 'publication']) ||
        (config.vendorAsPublisher ? product?.vendor || '' : ''),
      price,
      compareAtPrice,
      priceFormatted: formatMoney(price),
      compareAtPriceFormatted: compareAtPrice > price ? formatMoney(compareAtPrice) : '',
      discountAmountFormatted: discountAmount > 0 ? formatMoney(discountAmount) : '',
      discountPercent,
      variantId: variant?.id || snapshot?.variantId || '',
      available: Boolean(variant?.available ?? snapshot?.available),
      badge: snapshot?.badge || productBadge(product),
    };
  };

  const metadataHTML = (data) => {
    const entries = [data.author, data.language, data.publisher].filter(Boolean);
    if (!entries.length) return '';
    return `<div class="rd-book-card__meta" aria-label="Book information">${entries
      .map((entry, index) => `${index ? '<span class="rd-book-card__separator" aria-hidden="true">|</span>' : ''}<span>${escapeHTML(entry)}</span>`)
      .join('')}</div>`;
  };

  const badgeHTML = (badge) => {
    if (!badge) return '';
    return `<span class="rd-book-card__badge"><svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m10 2.6 2.05 4.16 4.59.67-3.32 3.23.78 4.57L10 13.08l-4.1 2.15.78-4.57-3.32-3.23 4.59-.67L10 2.6Z" fill="currentColor"/></svg><span>${escapeHTML(badge)}</span></span>`;
  };

  const spinnerHTML = () =>
    '<span class="loading__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';

  const dynamicCardHTML = (data, config, index) => {
    const hasDiscount = data.compareAtPrice > data.price;
    const discountLabel =
      config.discountStyle === 'percentage'
        ? `${data.discountPercent}% Off`
        : `${data.discountAmountFormatted} Off`;
    const formId = `RD-WishlistForm-${data.id}-${index}`;
    const titleId = `RD-WishlistTitle-${data.id}-${index}`;
    const image = data.image
      ? `<img class="rd-book-card__image" src="${escapeHTML(data.image)}" alt="${escapeHTML(data.title)}" loading="lazy" width="720" height="900">`
      : '<div class="rd-book-card__placeholder"></div>';

    const snapshot = JSON.stringify({
      ...data,
      discountStyle: config.discountStyle,
    }).replaceAll('<', '\\u003c');

    return `
      <article class="rd-book-card" data-rd-book-card data-product-handle="${escapeHTML(data.handle)}" data-product-id="${escapeHTML(data.id)}">
        <div class="rd-book-card__media">
          <a class="rd-book-card__image-link" href="${escapeHTML(data.url)}" aria-labelledby="${titleId}">${image}</a>
          ${badgeHTML(data.badge)}
          <button type="button" class="rd-book-card__wishlist" data-rd-wishlist-toggle data-product-handle="${escapeHTML(data.handle)}" aria-label="Remove ${escapeHTML(data.title)} from wishlist" aria-pressed="true">
            <svg class="rd-book-card__heart rd-book-card__heart--outline" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <svg class="rd-book-card__heart rd-book-card__heart--filled" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" fill="currentColor"/></svg>
          </button>
        </div>
        <div class="rd-book-card__content">
          <h3 class="rd-book-card__title" id="${titleId}"><a href="${escapeHTML(data.url)}">${escapeHTML(data.title)}</a></h3>
          ${metadataHTML(data)}
          <div class="rd-book-card__price-row">
            <div class="rd-book-card__prices">
              ${hasDiscount ? `<s class="rd-book-card__compare-price">${escapeHTML(data.compareAtPriceFormatted)}</s>` : ''}
              <span class="rd-book-card__price">${escapeHTML(data.priceFormatted)}</span>
            </div>
            ${hasDiscount ? `<span class="rd-book-card__discount">${escapeHTML(discountLabel)}</span>` : ''}
          </div>
          <product-form class="rd-book-card__product-form" data-section-id="wishlist">
            <form method="post" action="${escapeHTML(window.routes?.cart_add_url || '/cart/add')}" id="${formId}" class="rd-book-card__form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${escapeHTML(data.variantId)}" class="product-variant-id" ${data.available ? '' : 'disabled'}>
              <input type="hidden" name="quantity" value="1">
              <button type="submit" name="add" class="rd-book-card__add" aria-labelledby="${formId}-label ${titleId}" data-sold-out-message="true" ${data.available ? '' : 'disabled'}>
                <span id="${formId}-label">${escapeHTML(data.available ? config.addLabel : config.soldOutLabel)}</span>
                <span class="rd-book-card__bag-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M6.5 8.5h11l1 11h-13l1-11Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 9V6.75A3 3 0 0 1 12 3.8a3 3 0 0 1 3 2.95V9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M9.4 12.2c.5.62 1.45 1.05 2.6 1.05s2.1-.43 2.6-1.05" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>
                <span class="sold-out-message hidden">${escapeHTML(config.soldOutLabel)}</span>
                ${spinnerHTML()}
              </button>
              <div class="product-form__error-message-wrapper rd-book-card__error" role="alert" hidden><span class="product-form__error-message"></span></div>
            </form>
          </product-form>
        </div>
        <script type="application/json" data-rd-book-snapshot>${snapshot}</script>
      </article>`;
  };

  const wishlistConfig = (content) => ({
    emptyHeading: content.dataset.emptyHeading || 'Your wishlist is empty',
    emptyText: content.dataset.emptyText || 'Save books with the heart icon and they will appear here.',
    shopLabel: content.dataset.shopLabel || 'Explore books',
    shopUrl: content.dataset.shopUrl || normalizeRoot(),
    addLabel: content.dataset.addLabel || 'Add to Bag',
    soldOutLabel: content.dataset.soldOutLabel || 'Sold out',
    discountStyle: content.dataset.discountStyle || 'amount',
    vendorAsPublisher: content.dataset.vendorAsPublisher === 'true',
    columnsDesktop: content.dataset.columnsDesktop || '4',
    columnsTablet: content.dataset.columnsTablet || '3',
    columnsMobile: content.dataset.columnsMobile || '2',
  });

  const renderEmptyWishlist = (content, config) => {
    content.innerHTML = `
      <div class="rd-wishlist-page__status">
        <div class="rd-wishlist-page__empty">
          <h2>${escapeHTML(config.emptyHeading)}</h2>
          <p>${escapeHTML(config.emptyText)}</p>
          <a class="rd-wishlist-page__shop-link" href="${escapeHTML(config.shopUrl)}">${escapeHTML(config.shopLabel)}</a>
        </div>
      </div>`;
  };

  const renderWishlistPage = async (page) => {
    const content = page.querySelector('[data-rd-wishlist-content]');
    if (!content) return;

    const renderToken = String(Date.now() + Math.random());
    content.dataset.renderToken = renderToken;
    const config = wishlistConfig(content);
    const handles = getWishlist();

    if (!handles.length) {
      renderEmptyWishlist(content, config);
      return;
    }

    content.innerHTML = '<div class="rd-wishlist-page__status">Loading your saved books…</div>';
    const meta = getWishlistMeta();

    const products = await Promise.all(
      handles.map(async (handle) => {
        const product = await getProduct(handle);
        return combineProductData(handle, product, meta[handle], config);
      }),
    );

    if (content.dataset.renderToken !== renderToken) return;

    const validProducts = products.filter((product) => product.variantId || product.url);
    if (!validProducts.length) {
      renderEmptyWishlist(content, config);
      return;
    }

    content.innerHTML = `
      <div class="rd-books-grid rd-books-grid--desktop-${config.columnsDesktop} rd-books-grid--tablet-${config.columnsTablet} rd-books-grid--mobile-${config.columnsMobile}">
        ${validProducts.map((product, index) => dynamicCardHTML(product, config, index)).join('')}
      </div>`;

    updateWishlistUI();
  };

  const initWishlistPages = (scope = document) => {
    scope.querySelectorAll?.('[data-rd-wishlist-page]').forEach((page) => {
      if (page.dataset.rdWishlistPageInitialised === 'true') return;
      page.dataset.rdWishlistPageInitialised = 'true';
      renderWishlistPage(page);

      document.addEventListener('rd:wishlist:changed', () => renderWishlistPage(page));
      window.addEventListener('storage', (event) => {
        if (event.key === WISHLIST_KEY) renderWishlistPage(page);
      });
    });
  };

  document.addEventListener('click', syncSnapshotAfterWishlistClick, true);

  const init = (scope = document) => {
    window.setTimeout(installWishlistFallback, 0);
    initWishlistPages(scope);
    updateWishlistUI();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
